import {
  Suspense,
  useMemo,
} from 'react';
import { Canvas } from '@react-three/fiber';
import {
  ContactShadows,
  Environment,
  OrbitControls,
  RoundedBox,
} from '@react-three/drei';
import { DoubleSide } from 'three';

const DEG_TO_RAD =
  Math.PI / 180;

const SCALE = 0.01;

const toScene = (value) =>
  Number(value || 0) * SCALE;

const materialProps = (part) => {
  const material = String(
    part.material || '',
  ).toLowerCase();

  if (
    material === 'metal' ||
    material === 'brass'
  ) {
    return {
      color:
        part.color ||
        '#b6bdc3',
      metalness: 0.88,
      roughness: 0.24,
      clearcoat: 0.35,
      transparent:
        (part.opacity ?? 1) <
        1,
      opacity:
        part.opacity ?? 1,
    };
  }

  if (material === 'wood') {
    return {
      color:
        part.color ||
        '#8a6545',
      metalness: 0,
      roughness: 0.68,
      clearcoat: 0.1,
      transparent:
        (part.opacity ?? 1) <
        1,
      opacity:
        part.opacity ?? 1,
    };
  }

  if (material === 'acrylic') {
    return {
      color:
        part.color ||
        '#ffffff',
      metalness: 0,
      roughness: 0.13,
      transmission: 0.68,
      thickness: 0.35,
      ior: 1.46,
      clearcoat: 0.82,
      clearcoatRoughness: 0.1,
      transparent: true,
      opacity:
        part.opacity ?? 0.76,
    };
  }

  if (material === 'rubber') {
    return {
      color:
        part.color ||
        '#1f2325',
      metalness: 0,
      roughness: 0.92,
      transparent:
        (part.opacity ?? 1) <
        1,
      opacity:
        part.opacity ?? 1,
    };
  }

  return {
    color:
      part.color ||
      '#ffffff',
    metalness: 0.03,
    roughness: 0.5,
    clearcoat: 0.14,
    transparent:
      (part.opacity ?? 1) <
      1,
    opacity:
      part.opacity ?? 1,
  };
};

function PartMaterial({
  props,
  selected,
}) {
  return (
    <meshPhysicalMaterial
      {...props}
      emissive={
        selected
          ? '#214b43'
          : '#000000'
      }
      emissiveIntensity={
        selected ? 0.12 : 0
      }
    />
  );
}

function PartMesh({
  part,
  selected,
  onSelect,
}) {
  const position =
    (part.position ||
      [0, 0, 0])
      .map(toScene);

  const rotation =
    (part.rotation ||
      [0, 0, 0])
      .map(
        (value) =>
          Number(value || 0) *
          DEG_TO_RAD,
      );

  const size =
    (part.size ||
      [1, 1, 1])
      .map(toScene);

  const props =
    materialProps(part);

  const handleSelect =
    (event) => {
      event.stopPropagation();
      onSelect(part.id);
    };

  if (
    part.type ===
    'cylinder'
  ) {
    const diameter = size[0];
    const height = size[1];

    return (
      <mesh
        position={position}
        rotation={rotation}
        onClick={handleSelect}
        castShadow
        receiveShadow
      >
        <cylinderGeometry
          args={[
            diameter / 2,
            diameter / 2,
            height,
            64,
          ]}
        />

        <PartMaterial
          props={props}
          selected={selected}
        />
      </mesh>
    );
  }

  if (
    part.type === 'plane'
  ) {
    return (
      <mesh
        position={position}
        rotation={rotation}
        onClick={handleSelect}
      >
        <planeGeometry
          args={[
            size[0],
            size[1],
          ]}
        />

        <meshPhysicalMaterial
          {...props}
          side={DoubleSide}
          emissive={
            selected
              ? '#214b43'
              : '#000000'
          }
          emissiveIntensity={
            selected
              ? 0.12
              : 0
          }
        />
      </mesh>
    );
  }

  const radius =
    Math.max(
      0,
      Number(part.radius || 0),
    ) * SCALE;

  if (radius > 0) {
    return (
      <RoundedBox
        args={size}
        radius={Math.min(
          radius,
          Math.min(...size) /
            2.4,
        )}
        smoothness={5}
        position={position}
        rotation={rotation}
        onClick={handleSelect}
        castShadow
        receiveShadow
      >
        <PartMaterial
          props={props}
          selected={selected}
        />
      </RoundedBox>
    );
  }

  return (
    <mesh
      position={position}
      rotation={rotation}
      onClick={handleSelect}
      castShadow
      receiveShadow
    >
      <boxGeometry
        args={size}
      />

      <PartMaterial
        props={props}
        selected={selected}
      />
    </mesh>
  );
}

function Scene({
  definition,
  selectedPartId,
  onSelectPart,
}) {
  const groundY =
    useMemo(() => {
      const values =
        (definition.parts || [])
          .map(
            (part) =>
              toScene(
                part
                  .position?.[1],
              ) -
              toScene(
                part.size?.[1],
              ) /
                2,
          );

      return Math.min(
        ...values,
        -0.1,
      ) - 0.08;
    }, [definition.parts]);

  return (
    <>
      <ambientLight
        intensity={0.8}
      />

      <directionalLight
        position={[
          4.5,
          6.5,
          4.5,
        ]}
        intensity={2.2}
        castShadow
      />

      <directionalLight
        position={[-3, 2, 2]}
        intensity={0.75}
      />

      <Environment
        preset="studio"
      />

      <group>
        {(definition.parts || []).map(
          (part) => (
            <PartMesh
              key={part.id}
              part={part}
              selected={
                part.id ===
                selectedPartId
              }
              onSelect={
                onSelectPart
              }
            />
          ),
        )}
      </group>

      <ContactShadows
        position={[
          0,
          groundY,
          0,
        ]}
        opacity={0.25}
        scale={5}
        blur={2.7}
        far={4}
      />

      <OrbitControls
        makeDefault
        enablePan={false}
        minDistance={2.35}
        maxDistance={7}
        minPolarAngle={0.8}
        maxPolarAngle={1.72}
      />
    </>
  );
}

export default function Admin3DScene({
  definition,
  selectedPartId,
  onSelectPart,
}) {
  return (
    <div className="qr-print-admin-3d-scene">
      <Canvas
        shadows
        dpr={[1, 1.8]}
        camera={{
          position: [
            2.8,
            2.0,
            3.8,
          ],
          fov: 34,
        }}
      >
        <color
          attach="background"
          args={['#f4f7f6']}
        />

        <Suspense
          fallback={null}
        >
          <Scene
            definition={definition}
            selectedPartId={
              selectedPartId
            }
            onSelectPart={
              onSelectPart
            }
          />
        </Suspense>
      </Canvas>
    </div>
  );
}